import { Avatar, Button } from "@garther/ui/components";
import { useDropdown } from "@garther/ui/hooks";
import { cn, useI18n } from "@garther/ui/libs";
import { useRouter } from "@tanstack/react-router";
import { Calendar, CircleCheckBig, LogOut, User, Wallet } from "lucide-react";

import { trackEngagement } from "@/libs/analytics";
import { firebaseService } from "@/libs/firebase";
import { useAuthStore } from "@/stores/auth.store";

export function AuthButton() {
  const { loggedIn, curUser } = useAuthStore();
  const { isOpen, setIsOpen, dropdownRef } = useDropdown({ closeAfterClick: true });
  const router = useRouter();
  const { t } = useI18n();

  const handleSignOut = async () => {
    trackEngagement("sign_out", "authentication");
    await firebaseService.signOut();
    router.navigate({ to: "/" });
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  if (loggedIn) {
    return (
      <div className="relative w-10 h-9" ref={dropdownRef}>
        {/* User Profile Button */}
        <button onClick={toggleDropdown} className="pr-1 w-10 h-9">
          {/* User Avatar */}
          <Avatar
            name={curUser?.fbUser?.email || undefined}
            src={curUser?.profile?.avatar || undefined}
            size="full"
          />
          {/* User Email */}
          {/* <span className="text-text-primary text-sm hidden sm:block">{curUser?.profile?.name}</span> */}
          {/* Dropdown Arrow */}
          <div className="absolute right-0 bottom-0 bg-background-primary rounded-full border border-border-light">
            <svg
              className={cn(
                "w-3 h-3 text-text-secondary transition-transform duration-200",
                isOpen ? "rotate-180" : ""
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-[195px] bg-background-primary backdrop-blur-md rounded-sm border border-border-light shadow-xl z-50 text-responsive-base">
            <div className="py-1">
              {/* Profile Option */}
              <button
                className="w-full text-left px-4 py-2.5 text-text-primary hover:bg-background-highlight transition-colors duration-200 flex items-center space-x-3"
                onClick={() =>
                  router.navigate({
                    to: "/user/$userId/profile",
                    params: { userId: curUser?.fbUser?.uid || "" }
                  })
                }
              >
                <User size={18} strokeWidth={2} />
                <span>{t("navigation.viewProfile")}</span>
              </button>

              {/* Wallet Option */}
              <button
                className="w-full text-left px-4 py-2.5 text-text-primary hover:bg-background-highlight transition-colors duration-200 flex items-center space-x-3"
                onClick={() =>
                  router.navigate({
                    to: "/user/$userId/wallet",
                    params: { userId: curUser?.fbUser?.uid || "" }
                  })
                }
              >
                <Wallet size={18} strokeWidth={2} />
                <span>{t("navigation.wallet")}</span>
              </button>

              {/* My Bookings Option */}
              <button
                className="w-full text-left px-4 py-2.5 text-text-primary hover:bg-background-highlight transition-colors duration-200 flex items-center space-x-3"
                onClick={() =>
                  router.navigate({
                    to: "/user/$userId/booking",
                    params: { userId: curUser?.fbUser?.uid || "" }
                  })
                }
              >
                <Calendar size={18} strokeWidth={2} />
                <span>{t("navigation.myBookings")}</span>
              </button>

              {/* Divider */}
              <div className="border-t border-border-light my-1"></div>

              {/* Sign Out Option */}
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-2.5 text-red-500 hover:bg-red-500/10 transition-colors duration-200 flex items-center space-x-3"
              >
                <LogOut size={18} strokeWidth={2} />
                <span>{t("navigation.signOut")}</span>
              </button>
            </div>
          </div>
        )}

        {/* Click outside to close dropdown */}
        {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />}
      </div>
    );
  }

  return (
    /* Sign In Button */
    <Button
      onClick={() => {
        trackEngagement("sign_in_click", "authentication");
        firebaseService.signIn();
      }}
      className="flex items-center space-x-1 md:space-x-2 text-responsive-base px-2 py-1.5 md:px-3 md:py-2"
      variant="outline"
    >
      <CircleCheckBig size={16} />
      <span>{t("navigation.login")}</span>
    </Button>
  );
}
